import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Building2, 
  Upload, 
  Trash2, 
  Image as ImageIcon, 
  Loader2,
  CheckCircle,
  FileImage,
  Info
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CabinetSettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cabinet, setCabinet] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCabinetInfo();
  }, []);

  const fetchCabinetInfo = async () => {
    try {
      const cabinetId = user?.cabinet_id;
      if (!cabinetId) {
        toast.error("Impossible de trouver les informations du cabinet");
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/cabinets/${cabinetId}`);
      setCabinet(response.data);
      
      // Check if logo exists
      if (response.data.logo_filename) {
        setLogoPreview(`${API_URL}/cabinets/${cabinetId}/logo?t=${Date.now()}`);
      }
    } catch (error) {
      console.error(/* PROD */ "Erreur lors du chargement des informations:", error);
      toast.error("Impossible de charger les informations du cabinet");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Type de fichier non autorisé. Utilisez PNG, JPG ou WebP.");
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Le fichier est trop volumineux. Maximum 2MB.");
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    uploadLogo(file);
  };

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API_URL}/cabinets/${user.cabinet_id}/upload-logo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Logo téléchargé avec succès!");
      
      // Update preview with actual URL
      setLogoPreview(`${API_URL}/cabinets/${user.cabinet_id}/logo?t=${Date.now()}`);
      
      // Update cabinet info
      setCabinet((prev) => ({
        ...prev,
        logo_filename: response.data.logo_filename,
      }));
    } catch (error) {
      console.error(/* PROD */ "Erreur lors du téléchargement:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du téléchargement du logo");
      // Reset preview if upload failed
      if (cabinet?.logo_filename) {
        setLogoPreview(`${API_URL}/cabinets/${user.cabinet_id}/logo?t=${Date.now()}`);
      } else {
        setLogoPreview(null);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/cabinets/${user.cabinet_id}/logo`);
      
      toast.success("Logo supprimé avec succès!");
      setLogoPreview(null);
      setCabinet((prev) => ({
        ...prev,
        logo_filename: null,
      }));
      setShowDeleteDialog(false);
    } catch (error) {
      console.error(/* PROD */ "Erreur lors de la suppression:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression du logo");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="cabinet-settings-loading">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-32 w-32 rounded-lg" />
              <Skeleton className="h-10 w-48" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!cabinet) {
    return (
      <div className="p-6" data-testid="cabinet-settings-error">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-gray-500">Impossible de charger les informations du cabinet.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="cabinet-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building2 className="w-6 h-6 text-[#1B4D3E]" />
          Paramètres du Cabinet
        </h1>
        <p className="text-gray-500 mt-1">
          Gérez les informations et le logo de votre cabinet
        </p>
      </div>

      {/* Cabinet Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informations du Cabinet</CardTitle>
          <CardDescription>
            Ces informations sont affichées sur vos rapports et documents officiels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-500">Nom du Cabinet</Label>
              <p className="font-medium">{cabinet.nom}</p>
            </div>
            <div>
              <Label className="text-gray-500">ID IGT</Label>
              <p className="font-medium">{cabinet.id_igt}</p>
            </div>
            <div>
              <Label className="text-gray-500">Responsable</Label>
              <p className="font-medium">{cabinet.responsable_prenom} {cabinet.responsable_nom}</p>
            </div>
            <div>
              <Label className="text-gray-500">Email</Label>
              <p className="font-medium">{cabinet.email}</p>
            </div>
            <div>
              <Label className="text-gray-500">Région</Label>
              <p className="font-medium">{cabinet.region || "Non spécifiée"}</p>
            </div>
            <div>
              <Label className="text-gray-500">Statut</Label>
              <p className="font-medium">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                  cabinet.status === "actif" 
                    ? "bg-green-100 text-green-700" 
                    : "bg-gray-100 text-gray-700"
                }`}>
                  {cabinet.status === "actif" ? (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      Actif
                    </>
                  ) : cabinet.status}
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logo Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-[#1B4D3E]" />
            Logo du Cabinet
          </CardTitle>
          <CardDescription>
            Ce logo sera affiché sur vos rapports PDF (quittances, bilans exportés, etc.)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Info banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Recommandations pour le logo :</p>
                <ul className="mt-1 list-disc list-inside space-y-1 text-blue-700">
                  <li>Format carré recommandé (ex: 500x500 pixels)</li>
                  <li>Formats acceptés : PNG, JPG, WebP</li>
                  <li>Taille maximale : 2 MB</li>
                  <li>Fond transparent recommandé pour une meilleure intégration</li>
                </ul>
              </div>
            </div>

            {/* Logo preview and upload */}
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Preview area */}
              <div className="flex-shrink-0">
                <div 
                  className={`w-40 h-40 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden ${
                    logoPreview ? "border-[#1B4D3E] bg-white" : "border-gray-300 bg-gray-50"
                  }`}
                >
                  {uploading ? (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E] mx-auto" />
                      <p className="text-xs text-gray-500 mt-2">Téléchargement...</p>
                    </div>
                  ) : logoPreview ? (
                    <img 
                      src={logoPreview} 
                      alt="Logo du cabinet" 
                      className="w-full h-full object-contain p-2"
                      data-testid="cabinet-logo-preview"
                    />
                  ) : (
                    <div className="text-center p-4">
                      <FileImage className="w-10 h-10 text-gray-400 mx-auto" />
                      <p className="text-xs text-gray-500 mt-2">Aucun logo</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex-1 space-y-4">
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".png,.jpg,.jpeg,.webp"
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-[#1B4D3E] hover:bg-[#163d32]"
                    data-testid="upload-logo-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Téléchargement...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {logoPreview ? "Changer le logo" : "Télécharger un logo"}
                      </>
                    )}
                  </Button>
                </div>

                {logoPreview && cabinet?.logo_filename && (
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={deleting}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-logo-btn"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Suppression...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer le logo
                      </>
                    )}
                  </Button>
                )}

                {cabinet?.logo_filename && (
                  <p className="text-xs text-gray-500">
                    Dernier téléchargement : {cabinet.logo_updated_at 
                      ? new Date(cabinet.logo_updated_at).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })
                      : "Date inconnue"
                    }
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le logo ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le logo de votre cabinet ? 
              Cette action est irréversible et le logo ne sera plus affiché sur vos rapports PDF.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLogo}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                "Supprimer"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
